import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import propTypes from 'prop-types';

import { DatePicker, Dropdown, ElasticFormModal, Fetching, FormRow, Label, Radio, RadioGroup, TextField, TimeField } from 'basic-components';
import { DataStorageEnums, GetDataBody, Key } from 'dashboard-services/api/dataStorage';
import { DataTransformEnums } from 'dashboard-services/api/dataTransform';
import Query from 'dashboard-services/api/Query';
import TimeService from 'dashboard-services/services/TimeService';

import getApiConfig from 'api/ApiConfig';
import { basicCurvesQueries } from 'queries/curves';
import { dataStorageQueries } from 'queries/datastorage';
import FORMULAS from 'resources/constants/Formulas.js';
import { ExcelUtils, GAUtils, TimeSeriesUtils } from 'utils';

import NoColumnsWarning from '../../Series/AddSeriesDatapointModal/NoColumnsWarning';

import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import timezone from 'dayjs/plugin/timezone';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import { When } from 'react-if';

import './AddCurveDatapointModal.scss';

const MONTH_PERIOD_FORMAT = "YYYY-MM"
const getAbsolutePeriodValue = ({ periodType, absolutePeriod, definedTime }) => {
  if (dayjs(absolutePeriod, TimeService.ISO_DATE_FORMAT, true).isValid()) {
    dayjs.extend(quarterOfYear)
    dayjs.extend(timezone)
    dayjs.extend(weekOfYear)
    const localDate = dayjs.tz(absolutePeriod, definedTime)
    switch (periodType) {
      default:
      case DataTransformEnums.PERIOD_TYPE.DAILY:
      case DataTransformEnums.PERIOD_TYPE.BOD:
        return absolutePeriod
      case DataTransformEnums.PERIOD_TYPE.WEEKLY:
      case DataTransformEnums.PERIOD_TYPE.BOW:
        return `${localDate.year()}-${localDate.week()}`
      case DataTransformEnums.PERIOD_TYPE.MONTH:
      case DataTransformEnums.PERIOD_TYPE.BOM:
        return localDate.format(MONTH_PERIOD_FORMAT)
      case DataTransformEnums.PERIOD_TYPE.QUARTER:
        return `${localDate.year()}-${localDate.quarter()}`
      case DataTransformEnums.PERIOD_TYPE.CALENDAR:
      case DataTransformEnums.PERIOD_TYPE.BOC:
        return localDate.year()
    }
  }
}

//TODO: Time removed from here and we append it at fetch level that means we assume every curve is daily for now
const useInsertCurveDatapoint = ({ dateUTC, root, groupName, selectedColumn, selectedCurves, periodType, relative, period, source, onClose }) => (
  useCallback(async () => {
    GAUtils.sendEvent({
      category: "action",
      action: "add_curve",
      label: "add"
    })
    const address = await ExcelUtils.getSelectedCellAddress()
    if(root) {
      ExcelUtils.addToFormulaCell({ address, formula: `=${FORMULAS.LOAD_FC_DATAPOINT}("${dateUTC}", "${groupName}", "${root}", "${selectedColumn}", "${periodType}", "${relative}", "${period}", "${source}")` })
      onClose()
      return
    }
    if(selectedCurves?.length) {
      const formulas = selectedCurves.map(curve => `=${FORMULAS.LOAD_FC_DATAPOINT}("${dateUTC}", "${curve.groupName}", "${curve.root}", "${selectedColumn}", "${periodType}", "${relative}", "${period}", "${curve.source}")`)
      ExcelUtils.addFormulasBelow({ startAddress: address, formulas })
      onClose()
    }
  }, [dateUTC, groupName, onClose, period, periodType, relative, root, selectedColumn, selectedCurves, source])
)

const getLastDatapointRequestBody = ({ groupName, root, selectedColumn, source }) => {
  if(!selectedColumn || !groupName || !root) {
    return undefined
  }

  let builder = new GetDataBody.Builder()
  const key = new Key.Builder()
    .withColumns([selectedColumn])
    .withMetadatas({ FuturesRoot: root })
    .withGroupName(groupName)
    .withPattern(true)

  builder = builder.withKey(key.build().serialize())
  builder = builder
    .withLastType(DataStorageEnums.LAST_TYPE.VALUE)
    .withLastTypeAmount(2)
    .withRange(DataStorageEnums.DATA_RANGE.LAST)
    .withTimeZone(TimeService.UTC)
    .withSource(source)

  return builder.build().serialize()
}



const PERIOD_RELATIVE_DEFAULT_VALUE = "1"
const PERIOD_RELATIVE_MIN_VALUE = 1
const PERIOD_RELATIVE_MAX_VALUE = 120
const EMPTY_ARRAY = []
const AddCurveDatapointModal = ({ onClose }) => {
  const dispatch = useDispatch(),
        selectedCurves = useSelector(state => state.curvesState.creatorState.selectedCurves),
        source = useSelector(state => state.globalState.localSource),
        groupName = useSelector(state => state.curvesState.creatorState.groupName) || undefined,
        curveValues = useSelector(state => state.curvesState.creatorState.curveValues) || {},
        root = useSelector(state => state.curvesState.creatorState.root) || undefined,
        definedTime = useSelector(state => state.settingsState.definedTime) || undefined,
        [date, setDate] = useState(TimeService.getCurrentDate(definedTime)),
        [time, setTime] = useState(TimeService.getUserFormattedTime(date, definedTime)),
        [isRelative, setIsRelative] = useState("false"),
        [periodType, setPeriodType] = useState(DataTransformEnums.PERIOD_TYPE.MONTH),
        [period, setPeriod] = useState(PERIOD_RELATIVE_DEFAULT_VALUE),
        [absolutePeriod, setAbsolutePeriod] = useState(dayjs.tz(TimeService.getCurrentDate(definedTime), definedTime).startOf("month").format(TimeService.ISO_DATE_FORMAT)),
        timeoutRef = useRef(),
        onChangePeriodWithTimeout = useCallback((value, { name }) => {
          clearTimeout(timeoutRef.current)
          if (name === "periodRelative") {
            timeoutRef.current = setTimeout(() => setPeriod(value), 500)
          } else if (name === "periodAbsolute") {
            timeoutRef.current = setTimeout(() => setAbsolutePeriod(value), 500)
          }
        }, []),
        [selectedColumn, setSelectedColumn] = useState(undefined),
        selectedPeriodType = useMemo(() => DataTransformEnums.PERIOD_TYPE_ARRAY.find(p => p.value === periodType), [periodType]),
        dateUTC = useMemo(() => (dayjs(date, TimeService.ISO_DATE_FORMAT, true).isValid() && time) ? TimeService.getUTCDateTime(date + "T" + time, definedTime, TimeService.ISO_DATE_FORMAT) : undefined, [date, definedTime, time]),
        selectedCurve = useMemo(() => [{ groupName, product: curveValues.product, root, source }], [curveValues.product, groupName, root, source]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withHeader("Target", source, true)
        , [dispatch, source]),
        getTextQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withHeader("Target", source, true)
            .withOnParse(response => response.text())
            .withShouldDisplayError(false)
        , [dispatch, source]),
        { data: columns = EMPTY_ARRAY, isPending } = basicCurvesQueries.useGetColumns({ getQueryBuilder, selectedCurves: selectedCurve[0].root ? selectedCurve : selectedCurves, source }),
        periodForFormula = useMemo(() => {
          if (isRelative === "true") {
            return period
          } else {
            return getAbsolutePeriodValue({ periodType, absolutePeriod, definedTime })
          }
        }, [absolutePeriod, definedTime, isRelative, period, periodType]),
        insertDatapoint = useInsertCurveDatapoint({ dateUTC, groupName, root, selectedColumn, selectedCurves, periodType, relative: isRelative, period: periodForFormula, source, onClose }),
        isPeriodValid = useMemo(() => {
          if (isRelative === "true") {
            return period >= PERIOD_RELATIVE_MIN_VALUE && period <= PERIOD_RELATIVE_MAX_VALUE
          }
          else if (isRelative === "false") {
            return !!absolutePeriod && dayjs(absolutePeriod, TimeService.ISO_DATE_FORMAT, true).isValid()
          } else {
            return false
          }
        }, [absolutePeriod, isRelative, period]),
        requestTarget = useMemo(() => selectedCurves.length > 0 ? { groupName: selectedCurves[0]?.groupName, root: selectedCurves[0]?.root } : { groupName,root }, [groupName, root, selectedCurves]),
        requestBody = useMemo(() => getLastDatapointRequestBody({ groupName: requestTarget.groupName, root: requestTarget.root, selectedColumn, source }), [requestTarget.groupName, requestTarget.root, selectedColumn, source]),
        onSelectLastDatapoint = useCallback(response => TimeSeriesUtils.getFirstAppearingDate(response), []),
        { data: lastDatapointDateTimeUTC, isLoading: isLastDatapointLoading } = dataStorageQueries.useGetStreamData({
          getQueryBuilder: getTextQueryBuilder,
          body: requestBody,
          size: 1,
          enabled: !!requestBody,
          select: onSelectLastDatapoint
        }),
        userFormattedLastCurvepointDateTime = useMemo(() => TimeService.getUserFormattedDateTime(lastDatapointDateTimeUTC, definedTime), [definedTime, lastDatapointDateTimeUTC]),
        relativePeriodValidator = useMemo(() => [{
          message: `Period has to be between ${PERIOD_RELATIVE_MIN_VALUE} and ${PERIOD_RELATIVE_MAX_VALUE}`, 
          validator: val => Number(val) >= PERIOD_RELATIVE_MIN_VALUE && Number(val) <= PERIOD_RELATIVE_MAX_VALUE
        }], []),
        submitProps = useMemo(() => ({
          disabled: !date || !dayjs(date, TimeService.ISO_DATE_FORMAT, true).isValid() ||  !time || !selectedColumn || isRelative === undefined || !periodType || !isPeriodValid
        }), [date, isPeriodValid, isRelative, periodType, selectedColumn, time])

  useEffect(() => {
    if(columns.length) {
      setSelectedColumn(columns[0]?.value)
    }
  }, [columns])

    useEffect(() => {
    if (userFormattedLastCurvepointDateTime) {
      setDate(dayjs.tz(userFormattedLastCurvepointDateTime, definedTime).format(TimeService.ISO_DATE_FORMAT))
    }
  }, [definedTime, userFormattedLastCurvepointDateTime])

  return (
    <ElasticFormModal
        className="ng-office-app__authed__content__body__item__datapoint-modal"
        onClose={onClose}
        onSubmit={insertDatapoint}
        open
        submitProps={submitProps}
        submitText="Add to sheet"
        title="Add single data point"
        withFocusTrap
    >
      <Label
          className="ng-office-app__authed__content__body__item__datapoint-modal__label"
          required
          size="large"
      >
        Data point date and time
      </Label>
      <FormRow className="ng-office-app__authed__content__body__item__datapoint-modal__datetime-container">
        <DatePicker
            defaultValue={date}
            name="date"
            onChange={setDate}
            variant="border"
        />
        <TimeField
            defaultValue={time}
            name="time"
            onChange={setTime}
            variant="border"
            withSeconds
        />
      </FormRow>
      <Label 
          className="ng-office-app__authed__content__body__item__datapoint-modal__label"
          required
      >
        Column
      </Label>
      <div className="ng-office-app__authed__content__body__item__datapoint-modal__dropdown-container">
        <Dropdown
            creatable
            defaultValue={selectedColumn}
            disabled={!columns.length && !isPending}
            fixed
            labelId="column"
            name="selectedColumns"
            noError
            onChange={setSelectedColumn}
            options={columns}
            placeholder="Column"
            variant="border"
        />
      </div>
      <Label 
          className="ng-office-app__authed__content__body__item__datapoint-modal__label"
          required
      >
        Contract type
      </Label>
      <div className="ng-office-app__authed__content__body__item__datapoint-modal__radio-container">
        <RadioGroup
            name="isRelative"
            onChange={setIsRelative}
            selectedValue={isRelative}
        >
          <Radio name="false">
            Absolute
          </Radio>
          <Radio name="true">
            Relative
          </Radio>
        </RadioGroup>
      </div>
      <Label 
          className="ng-office-app__authed__content__body__item__datapoint-modal__label"
          required
      >
        Period
      </Label>
      <When condition={isRelative === "true"}>
        <div className="ng-office-app__authed__content__body__item__datapoint-modal__text-field-container">
          <FormRow>
            <TextField
                defaultValue={period}
                max={120}
                min={1}
                name="periodRelative"
                onChange={onChangePeriodWithTimeout}
                placeholder="Relative period value"
                step={1}
                type="number"
                validators={relativePeriodValidator}
                variant="border"
            />
          </FormRow>
        </div>
      </When>
      <When condition={isRelative === "false"}>
        <FormRow className="ng-office-app__authed__content__body__item__datapoint-modal__datetime-container">
          <DatePicker
              defaultValue={absolutePeriod}
              name="periodAbsolute"
              onChange={onChangePeriodWithTimeout}
              variant="border"
          />
        </FormRow>
      </When>
      <Label 
          className="ng-office-app__authed__content__body__item__datapoint-modal__label"
          required
      >
        Period type
      </Label>
      <div className="ng-office-app__authed__content__body__item__datapoint-modal__dropdown-container">
        <Dropdown
            defaultValue={selectedPeriodType}
            fixed
            name="periodType"
            noError
            onChange={setPeriodType}
            options={DataTransformEnums.PERIOD_TYPE_ARRAY}
            placeholder="Period type"
            variant="border"
        />
      </div>
      <div className="ng-office-app__authed__content__body__item__datapoint-modal__last-datapoint">
          <When condition={isLastDatapointLoading}>
            {() => (
              <Fetching 
                  loading={isLastDatapointLoading} 
                  size={32}
              />
            )}
          </When>
          <When condition={!isLastDatapointLoading}>
            {() => (
              <div>
                <span className="ng-office-app__authed__content__body__item__datapoint-modal__last-datapoint--muted-text">
                  {lastDatapointDateTimeUTC ? "Last data point: " : "Last data point not found."}
                </span>
                {lastDatapointDateTimeUTC ? userFormattedLastCurvepointDateTime : ""}
              </div>
            )}
          </When>
      </div>
      <When condition={columns.length === 0 && !isPending}>
        <NoColumnsWarning/>
      </When>
    </ElasticFormModal>
  )
}

AddCurveDatapointModal.propTypes = {
  onClose: propTypes.func.isRequired
}

export default AddCurveDatapointModal
